/**
 * LaunchPage — Dashboard SSO Launcher
 *
 * Ruta: /launch
 *
 * Muestra las herramientas externas disponibles y abre cada una con SSO.
 * Este componente es completamente independiente del resto de la app.
 * No usa Redux ni ningún contexto existente.
 */

// libraries
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';

// config & utils (rutas relativas — no hay alias @config/@utils)
import { EXTERNAL_TOOLS, ExternalTool, getCurrentEnvironment, getToolUrl } from '../../config/external-tools';
import { openExternalTool } from '../../utils/external-tool.service';

// ─────────────────────────────────────────────────────────────────────────────
// Llave que usa LoginPageLaunch para guardar el token
// ─────────────────────────────────────────────────────────────────────────────
const LAUNCH_TOKEN_KEY     = 'launch_token';
const LAUNCH_GOOGLE_ID_KEY = 'launch_googleId';

const hasLaunchAuth = (): boolean =>
  !!localStorage.getItem(LAUNCH_TOKEN_KEY) && !!localStorage.getItem(LAUNCH_GOOGLE_ID_KEY);

const clearLaunchAuth = (): void => {
  localStorage.removeItem(LAUNCH_TOKEN_KEY);
  localStorage.removeItem(LAUNCH_GOOGLE_ID_KEY);
};

// ─────────────────────────────────────────────────────────────────────────────
// Card de herramienta externa
// ─────────────────────────────────────────────────────────────────────────────
interface ToolCardProps {
  tool: ExternalTool;
  env: 'local' | 'qa' | 'prod';
  onOpen: (tool: ExternalTool) => void;
}

const ToolCard = ({ tool, env, onOpen }: ToolCardProps) => {
  const envColors: Record<string, string> = {
    local: '#f59e0b',
    qa:    '#3b82f6',
    prod:  '#10b981',
  };
  const envLabels: Record<string, string> = {
    local: 'Localhost',
    qa:    'QA (.llavero.co)',
    prod:  'Producción (.habi.co)',
  };
  const ssoMethod: Record<string, string> = {
    local: '🔗 URL params',
    qa:    '🍪 Cookies compartidas',
    prod:  '🍪 Cookies compartidas',
  };

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '16px',
        border: '1px solid #e5e7eb',
        padding: '2rem',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        height: '100%',
      }}
    >
      {/* Header */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <h5 style={{ margin: 0, fontWeight: 700, color: '#1f2937', fontFamily: "'Montserrat Bold', sans-serif" }}>{tool.name}</h5>
          <span
            style={{
              background: envColors[env] + '20',
              color: envColors[env],
              borderRadius: '20px',
              padding: '2px 10px',
              fontSize: '11px',
              fontWeight: 600,
              border: `1px solid ${envColors[env]}40`,
            }}
          >
            {envLabels[env]}
          </span>
        </div>
        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', fontFamily: "'Roboto', sans-serif" }}>{tool.description}</p>
      </div>

      {/* URL destino */}
      <div
        style={{
          background: '#f9fafb',
          borderRadius: '8px',
          padding: '10px 12px',
          fontSize: '12px',
          color: '#374151',
          wordBreak: 'break-all',
          border: '1px solid #e5e7eb',
        }}
      >
        <span style={{ color: '#9ca3af', fontWeight: 600, fontFamily: "'Roboto', sans-serif" }}>URL: </span>
        <span style={{ fontFamily: "'Roboto', sans-serif" }}>{getToolUrl(tool)}{tool.ssoPath}</span>
      </div>

      {/* Método SSO */}
      <div style={{ fontSize: '13px', color: '#6b7280', fontFamily: "'Roboto', sans-serif" }}>
        <strong>Método SSO:</strong> {ssoMethod[env]}
      </div>

      {/* Botón */}
      <button
        onClick={() => onOpen(tool)}
        style={{
          marginTop: 'auto',
          background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'opacity 0.2s',
          width: '100%',
          fontFamily: "'Montserrat Bold', sans-serif",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
      >
        Abrir con SSO →
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
export const LaunchPage = () => {
  const navigate    = useNavigate();
  const [ready, setReady]           = useState(false);
  const [openedTool, setOpenedTool] = useState<string | null>(null);
  const env = getCurrentEnvironment();

  // Verificar que haya auth guardada (vino del LoginPageLaunch)
  useEffect(() => {
    if (!hasLaunchAuth()) {
      console.warn('⚠️ [Launcher] No hay auth guardada. Redirigiendo a /launch-login');
      navigate('/launch-login', { replace: true });
      return;
    }
    setReady(true);
  }, [navigate]);

  const handleOpen = (tool: ExternalTool) => {
    setOpenedTool(tool.id);
    openExternalTool(tool);
    // Limpia el estado visual después de 2s
    setTimeout(() => setOpenedTool(null), 2000);
  };

  const handleLogout = () => {
    clearLaunchAuth();
    navigate('/launch-login', { replace: true });
  };

  if (!ready) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const envLabels: Record<string, string> = {
    local: '🏠 Localhost',
    qa:    '🧪 QA (.llavero.co)',
    prod:  '🏢 Producción (.habi.co)',
  };

  const envColors: Record<string, string> = {
    local: '#f59e0b',
    qa:    '#3b82f6',
    prod:  '#10b981',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#F3F3F3',
      }}
    >
      {/* Header */}
      <div
        style={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '1rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#5b21b6', fontFamily: "'Montserrat Bold', sans-serif" }}>
            🚀 SSO Launcher
          </h1>
          <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af', fontFamily: "'Roboto', sans-serif" }}>
            Pricing Rocket — Prueba de integración SSO
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Badge de ambiente */}
          <span
            style={{
              background: envColors[env] + '20',
              color: envColors[env],
              border: `1px solid ${envColors[env]}40`,
              borderRadius: '20px',
              padding: '4px 14px',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: "'Roboto', sans-serif",
            }}
          >
            {envLabels[env]}
          </span>

          {/* Botón salir */}
          <button
            onClick={handleLogout}
            style={{
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '6px 14px',
              fontSize: '13px',
              color: '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontFamily: "'Roboto', sans-serif",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#dc2626';
              e.currentTarget.style.color = '#dc2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            Salir del launcher
          </button>
        </div>
      </div>

      {/* Contenido */}
      <Container style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
        {/* Título */}
        <div style={{ marginBottom: '2.5rem', textAlign: 'center' }}>
          <h2 style={{ fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem', fontFamily: "'Montserrat Bold', sans-serif" }}>
            Herramientas externas
          </h2>
          <p style={{ color: '#6b7280', fontSize: '15px', fontFamily: "'Roboto', sans-serif" }}>
            Haz click en una herramienta para abrirla con auto-login SSO
          </p>
        </div>

        {/* Info de método SSO según ambiente */}
        {env === 'local' && (
          <div
            style={{
              background: '#fffbeb',
              border: '1px solid #fcd34d',
              borderRadius: '10px',
              padding: '14px 20px',
              marginBottom: '2rem',
              fontSize: '14px',
              color: '#92400e',
            }}
          >
            <strong style={{ fontFamily: "'Montserrat Bold', sans-serif" }}>⚠️ Modo Localhost:</strong>{' '}
            <span style={{ fontFamily: "'Roboto', sans-serif" }}>Los datos de autenticación se pasan por{' '}
            <strong>URL params</strong> ({`?_autoLogin=true&_token=...&_googleId=...`}).<br />
            Asegúrate de que las herramientas de destino estén corriendo en sus puertos locales.</span>
          </div>
        )}

        {(env === 'qa' || env === 'prod') && (
          <div
            style={{
              background: '#f0fdf4',
              border: '1px solid #86efac',
              borderRadius: '10px',
              padding: '14px 20px',
              marginBottom: '2rem',
              fontSize: '14px',
              color: '#14532d',
            }}
          >
            <strong style={{ fontFamily: "'Montserrat Bold', sans-serif" }}>✅ Modo {env === 'qa' ? 'QA' : 'PROD'}:</strong>{' '}
            <span style={{ fontFamily: "'Roboto', sans-serif" }}>Se usan{' '}
            <strong>cookies compartidas</strong> ({env === 'qa' ? 'domain=.llavero.co' : 'domain=.habi.co'}).{' '}
            Las herramientas se abren directamente con auto-login automático.</span>
          </div>
        )}

        {/* Cards de herramientas */}
        <Row className="g-4">
          {EXTERNAL_TOOLS.map((tool) => (
            <Col key={tool.id} xs={12} md={6} lg={4}>
              {openedTool === tool.id ? (
                <div
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    border: '2px solid #7c3aed',
                    padding: '2rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '1rem',
                    height: '100%',
                    minHeight: '200px',
                  }}
                >
                  <Spinner animation="border" style={{ color: '#7c3aed' }} />
                  <span style={{ color: '#5b21b6', fontWeight: 600, fontSize: '14px', fontFamily: "'Montserrat Bold', sans-serif" }}>
                    Abriendo {tool.name}...
                  </span>
                </div>
              ) : (
                <ToolCard tool={tool} env={env} onOpen={handleOpen} />
              )}
            </Col>
          ))}
        </Row>

        {/* Footer informativo — solo visible en localhost */}
        {env === 'local' && (
          <div
            style={{
              marginTop: '3rem',
              padding: '1.5rem',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              fontSize: '13px',
              color: '#6b7280',
            }}
          >
            <strong style={{ color: '#374151', fontFamily: "'Montserrat Bold', sans-serif" }}>📋 Cómo funciona:</strong>
            <ol style={{ margin: '0.75rem 0 0', paddingLeft: '1.5rem', lineHeight: '2', fontFamily: "'Roboto', sans-serif" }}>
              <li>Hiciste login en <code>/launch-login</code> → se guardó tu <code>token</code> + <code>googleId</code></li>
              <li>Al hacer click en una herramienta, se construye la URL de auto-login</li>
              <li>La herramienta destino lee los datos y hace el login automático</li>
              <li>
                Para que funcione en localhost, la herramienta destino debe implementar{' '}
                <code>/login-sso</code> y leer los parámetros <code>_autoLogin</code>, <code>_token</code>, <code>_googleId</code>
              </li>
            </ol>
          </div>
        )}
      </Container>
    </div>
  );
};

